// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Chip, Divider, Grid, IconButton, Typography } from '@mui/material';

// import DeleteIcon from '@mui/icons-material/Delete';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import BluetoothConnectedIcon from '@mui/icons-material/BluetoothConnected';
import BluetoothDisabledIcon from '@mui/icons-material/BluetoothDisabled';
import { useTranslation } from 'react-i18next';
import { FC } from 'react';
import { IconClipboardText, IconCalendarTime, IconBluetooth } from '@tabler/icons';
import NotesIcon from '@mui/icons-material/Notes';
import Halog from 'types/halog';
import useConfig from 'hooks/useConfig';
import Utilities from 'utils/utilities';
import darkThemeIcon from 'assets/images/halog-icon-dark-theme.png';
import lightThemeIcon from 'assets/images/halog-icon-light-theme.png';
import TagIcon from '@mui/icons-material/Tag';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AbcIcon from '@mui/icons-material/Abc';
import useAuth from 'hooks/useAuth';
import MobileSkeleton from 'ui-component/skeleton/MobileSkeleton';

interface HalogListMobileProps {
    halogList: Halog[] | undefined;
    onEdit: (halog: Halog) => void;
    onSwitch: (halog: Halog) => void;
    isLoading: boolean;
}

const HalogListMobile: FC<HalogListMobileProps> = (props) => {
    const { halogList, onEdit, onSwitch, isLoading } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { locale } = useConfig();
    const utilities = new Utilities();
    const { user } = useAuth();

    return (
        <Grid aria-labelledby="tableTitle">
            {!isLoading ? (
                <Grid>
                    {halogList!.map((halog) => {
                        if (typeof halog === 'number') return null;

                        return (
                            <Grid
                                key={halog.id}
                                sx={{
                                    display: 'block',
                                    '& .MuiGrid-root': { paddingRight: 0 }
                                }}
                            >
                                <Grid container spacing={1} sx={{ mt: 2, ml: 0, pl: 2 }}>
                                    <Grid item xs={12} sx={{ pl: 20 }}>
                                        <Grid container direction="column">
                                            <Grid container direction="row" style={{ flexFlow: 'noWrap' }} spacing={1} sx={{ mb: 1 }}>
                                                <Grid item>
                                                    {theme.palette.mode === 'dark' ? (
                                                        <img src={darkThemeIcon} style={{ width: '24px', height: '24px' }} alt="" />
                                                    ) : (
                                                        <img src={lightThemeIcon} style={{ width: '24px', height: '24px' }} alt="" />
                                                    )}
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {t('DEVICE')}:
                                                    </Typography>
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {halog.name ? halog.name : halog.identifier}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {halog.macAddress && (
                                        <Grid item xs={12} sx={{ pl: 20 }}>
                                            <Grid container direction="column">
                                                <Grid container direction="row" style={{ flexFlow: 'noWrap' }} spacing={1} sx={{ mb: 1 }}>
                                                    <Grid item>
                                                        <AbcIcon />
                                                    </Grid>
                                                    <Grid item sx={{ alignContent: 'center' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {t('MAC_ADDRESS')}:
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sx={{ alignContent: 'center' }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {halog.macAddress ? utilities.formatMacAddress(halog.macAddress) : '-'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sx={{ pl: 20 }}>
                                        <Grid container direction="column">
                                            <Grid container direction="row" style={{ flexFlow: 'noWrap' }} spacing={1} sx={{ mb: 1 }}>
                                                <Grid item>
                                                    <IconClipboardText />
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {t('ASSET_NAME')}:
                                                    </Typography>
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {halog.assetName ? halog.assetName : '-'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sx={{ pl: 20 }}>
                                        <Grid container direction="column">
                                            <Grid container direction="row" style={{ flexFlow: 'noWrap' }} spacing={1} sx={{ mb: 1 }}>
                                                <Grid item>
                                                    <IconBluetooth />
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {t('PAIRED_STATUS')}:
                                                    </Typography>
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {halog.pairDate && (
                                                            <BluetoothConnectedIcon sx={{ fontSize: '1.3rem' }} color="secondary" />
                                                        )}
                                                        {!halog.pairDate && (
                                                            <BluetoothDisabledIcon sx={{ fontSize: '1.3rem' }} color="error" />
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sx={{ pl: 10, pr: 5 }}>
                                        <Grid container direction="column">
                                            <Grid container direction="row" style={{ flexFlow: 'noWrap' }} spacing={1} sx={{ mb: 1 }}>
                                                <Grid item>
                                                    <IconCalendarTime />
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {t('PAIRED_DATE')}:
                                                    </Typography>
                                                </Grid>
                                                <Grid item sx={{ alignContent: 'center' }}>
                                                    <Typography
                                                        variant="body1"
                                                        sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                    >
                                                        {halog.pairDate ? utilities.formatDate(halog.pairDate, locale) : '-'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    {halog.tags && halog.tags.length > 0 && (
                                        <Grid item xs={12} sx={{ pl: 10, pr: 5 }}>
                                            <Grid container direction="column">
                                                <Grid
                                                    container
                                                    item
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{ mb: 1, flexFlow: 'noWrap', alignItems: 'flex-start' }}
                                                >
                                                    <Grid item>
                                                        <TagIcon />
                                                    </Grid>
                                                    <Grid item sx={{ alignContent: 'center' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {t('Tags')}:
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item container spacing={0.5} sx={{ alignItems: 'center' }}>
                                                        {halog.tags.map((value) => (
                                                            <Grid item key={value.id}>
                                                                <Chip color="secondary" label={value.name} size="small" />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {user?.isSystemAdmin && (
                                        <Grid item xs={12} sx={{ pl: 20 }}>
                                            <Grid container direction="column">
                                                <Grid container direction="row" style={{ flexFlow: 'noWrap' }} spacing={1} sx={{ mb: 1 }}>
                                                    <Grid item>
                                                        <NotesIcon />
                                                    </Grid>
                                                    <Grid item sx={{ alignContent: 'center' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {t('OWNER_MANUFACTURER_NOTE')}:
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item sx={{ alignContent: 'center' }}>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {halog.ownerManufacturerNote ? halog.ownerManufacturerNote : '-'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Divider sx={{ mx: 3 }} />
                                        <Grid
                                            container
                                            sx={{
                                                verticalAlign: 'bottom',
                                                textAlign: 'center',
                                                mt: 0.5
                                            }}
                                        >
                                            <Grid item xs={!halog.pairDate && user?.isSystemAdmin ? 6 : 12} sx={{ mb: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        color: 'secondary'
                                                    }}
                                                >
                                                    <IconButton color="secondary" size="large" onClick={() => onEdit(halog)}>
                                                        <EditTwoToneIcon sx={{ fontSize: '1.5rem' }} />
                                                    </IconButton>
                                                    {t('EDIT')}
                                                </Box>
                                            </Grid>
                                            {!halog.pairDate && user?.isSystemAdmin && (
                                                <Grid item xs={6} sx={{ mb: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            color: 'secondary'
                                                        }}
                                                    >
                                                        <IconButton color="secondary" size="large" onClick={() => onSwitch(halog)}>
                                                            <SwapHorizIcon sx={{ fontSize: '1.5rem' }} />
                                                        </IconButton>
                                                        {t('SWITCH_TENANT')}
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Divider sx={{ mx: 1 }} />
                            </Grid>
                        );
                    })}
                </Grid>
            ) : (
                <MobileSkeleton />
            )}
        </Grid>
    );
};

export default HalogListMobile;
